import moment from 'moment';
import NoImage from '@/assets/common/no-img.jpg';
import numeral from 'numeral';

/**
 * Formats a number as currency with 2 decimal places.
 * @param c - Value to format
 * @returns Formatted string (e.g. '1,234.56')
 */
export function formatCurrency(c?: string | number) {
  if (c) {
    return numeral(c).format('0,0.00');
  }

  return '0.00';
}

/**
 * Formats a number with comma separators (no decimals).
 * @param value - Value to format
 * @returns Formatted string (e.g. '1,234')
 */
export function formatComma(value: any) {
  if (value) {
    return numeral(value).format('0,0');
  }

  return '0';
}

/**
 * Formats ISO date string to Thai locale datetime.
 * @param isoDate - ISO date string
 * @param useBuddhistYear - Whether to use Buddhist calendar year
 * @returns Formatted datetime string
 */
export function formatDateTimeTH(
  isoDate: string,
  useBuddhistYear = false
): string {
  if (!isoDate) return '-';

  const date = new Date(isoDate);

  const locale = useBuddhistYear ? 'th-TH-u-ca-buddhist' : 'th-TH';

  return date.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Bangkok',
  });
}

/**
 * Converts date for default time handling (23:00/00:00 edge cases).
 * @param date - Date string
 * @param timeDefault - Optional time string ('23.00' or '00.00')
 * @param isStart - Whether this is start date
 * @returns Converted date string
 */
export function convertDefaultDate(
  date: string,
  timeDefault?: string,
  isStart?: boolean
) {
  let convertDate = date;
  const dateNow = new Date();

  if (timeDefault === '23.00') {
    convertDate = moment(date).subtract(1, 'd').format('YYYY-MM-DD');
    if (dateNow.getHours() === 23) {
      convertDate = moment(date).format('YYYY-MM-DD');
    }
  }

  if (timeDefault === '00.00') {
    convertDate = moment(date).subtract(1, 'd').format('YYYY-MM-DD');
    if (isStart) {
      convertDate = moment(date).format('YYYY-MM-DD');
    }
    // if (dateNow.getHours() === 23) {
    //   convertDate = moment(date).format('YYYY-MM-DD');
    // }
  }

  return convertDate;
}

/**
 * Handles image load error by setting fallback no-image src.
 * @param e - Image error event
 */
export function handleErrorImage(e: any) {
  e.target.onerror = null;
  e.target.src = NoImage;
  e.currentTarget.parentNode.children[0].srcset = NoImage;
}

/**
 * Gets current timezone offset string (e.g. '+07:00').
 * @returns Timezone offset in format ±HH:mm
 */
export function getTimezoneOffset() {
  const timedifference = moment.parseZone(moment().format()).format('Z');
  return timedifference;
}

/**
 * Combines date and time with timezone for API requests.
 * @param date - Date string
 * @param time - Time string
 * @returns ISO-like string with timezone
 */
export function dateWithTimezone(date: any, time: any) {
  return `${date}T${time}:00${getTimezoneOffset()}`;
}

/**
 * Returns list of supported languages for UI.
 * @returns Array of { key, label } objects
 */
export function LanguageLists() {
  const lng = [
    {
      key: 'en',
      label: 'English',
    },
    {
      key: 'th',
      label: 'ภาษาไทย',
    },
  ];

  return lng;
}

/**
 * Normalizes language code to locale key.
 * @param lng - Language/locale identifier
 * @returns Normalized locale key ('en', 'th', 'my', etc.)
 */
export function langRenderValiable(lng: string) {
  switch (lng.toLowerCase()) {
    case 'TH':
    case 'THB':
    case 'th-TH':
    case 'th':
      return 'th';

    case 'MMK':
    case 'MK':
    case 'mmk':
    case 'mk':
    case 'my':
      return 'my';

    case 'VND':
    case 'vnd':
    case 'vn':
    case 'vi':
      return 'vnd';

    case 'lo':
    case 'LO':
      return 'lo';

    case 'id':
      return 'id';

    case 'km':
      return 'km';

    case 'tr':
      return 'tr';

    case 'ko':
      return 'ko';

    case 'sv':
      return 'sv';

    case 'bn':
      return 'bn';

    case 'es':
      return 'es';

    case 'pt':
      return 'pt';

    default:
      return 'en';
  }
}

/**
 * Builds date range object for API from start/end date and time.
 * @param startDate - Start date string
 * @param endDate - End date string
 * @param startTime - Start time string
 * @param endTime - End time string
 * @returns Object with start_date/end_date or empty string
 */
export function handleDateOnFirstGet(
  startDate: string,
  endDate: string,
  startTime: string,
  endTime: string
) {
  if (startDate && endDate && startTime && endTime) {
    return {
      start_date: dateWithTimezone(startDate, startTime),
      end_date: dateWithTimezone(endDate, endTime),
    };
  }

  return '';
}

/**
 * Maps status string to Badge color variant.
 * @param status - Status value (e.g. SUCCESS, PENDING, CANCEL)
 * @returns Color variant ('success', 'warning', 'danger', 'inactive')
 */
export function renderStatus(status: string) {
  if (status) {
    switch (status.toUpperCase()) {
      case 'CANCEL':
      case 'OUT':
        return 'danger';

      case 'SUCCESS':
      case 'IN':
        return 'success';

      case 'PENDING':
        return 'warning';

      default:
        return 'inactive';
    }
  }

  return 'inactive';
}

/**
 * Format Date object to DD-MM-YYYY string format
 * @param date - Date object to format
 * @returns Formatted date string (DD-MM-YYYY)
 */
export function formatDateToQueryString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${d}-${m}-${y}`;
}

/**
 * Formats Date to DD/MM/YYYY string.
 * @param date - Date object
 * @returns Formatted date string
 */
export const formatDateDDMMYYYY = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Convert params object to URL query string
 * Filters out undefined, null, and empty string values
 * Automatically converts Date objects to DD-MM-YYYY format
 * @param params - Object containing query parameters
 * @returns URL query string (without leading ?)
 */
export function buildQueryString(params: Record<string, any>): string {
  const query: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    // Skip undefined, null, and empty string values
    if (value === undefined || value === '' || value === null) {
      return;
    }

    // Convert Date objects to formatted string
    if (value instanceof Date) {
      query[key] = formatDateToQueryString(value);
    } else {
      query[key] = String(value);
    }
  });

  return new URLSearchParams(query).toString();
}

/**
 * Builds a plain JSON body from a params object for use in POST search requests.
 * Similar to buildQueryParams but returns a plain object instead of URLSearchParams.
 * - Handles arrays of objects with { label, value } (e.g., from react-select) → extracts IDs as string[]
 * - Skips "All" markers (empty-value items in arrays)
 * - Handles Date objects with proper formatting
 * - Filters out undefined, null, empty strings
 */
export const buildPostBody = (
  params: Record<string, any>
): Record<string, any> => {
  const body: Record<string, any> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    // Skip single object with empty value (All marker)
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      'value' in value &&
      value.value === ''
    ) {
      return;
    }

    if (Array.isArray(value)) {
      // Check for "All" marker in array
      const hasAll = value.some(
        (item: any) =>
          item === '' ||
          (typeof item === 'object' &&
            item !== null &&
            'value' in item &&
            item.value === '')
      );
      if (hasAll) return;

      // Extract IDs from { value, label } objects or plain strings
      const ids = value
        .map((item: any) => {
          if (item === undefined || item === null || item === '') return null;
          if (typeof item === 'object' && item !== null && 'value' in item) {
            return item.value ? String(item.value).trim() : null;
          }
          return String(item).trim();
        })
        .filter(Boolean);

      if (ids.length > 0) {
        body[key] = ids;
      }
      return;
    }

    if (value instanceof Date) {
      body[key] = formatDateToQueryString(value);
      return;
    }

    body[key] = typeof value === 'string' ? value.trim() : value;
  });

  return body;
};

/**
 * Build URLSearchParams from a parameter object.
 * Handles:
 * - Primitives (string, number, boolean)
 * - Dates (converted to DD-MM-YYYY)
 * - Arrays of primitives (appends multiple keys)
 * - Arrays of objects with 'value' property (appends multiple keys)
 * - Filters out undefined, null, empty strings
 */
export const buildQueryParams = (
  params: Record<string, any>
): URLSearchParams => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    // Check for 'ALL' value first. If present, we skip appending this key entirely,
    // which signifies "no filter" (return all results) to the backend.
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (typeof value === 'object' &&
        value !== null &&
        'value' in value &&
        value.value === '')
    ) {
      return;
    }

    if (Array.isArray(value)) {
      // If the array contains 'ALL' (primitive or object val), ignore the whole list.
      // This implies "Select All" was chosen, so we send no ID params.
      const hasAll = value.some(
        (item) =>
          item === '' ||
          (typeof item === 'object' &&
            item !== null &&
            'value' in item &&
            item.value === '')
      );

      if (hasAll) {
        return;
      }

      // Comma-separated IDs: &supplier_ids=id1,id2 &game_package_ids=id1,id2
      if (
        key === 'supplier_ids' ||
        key === 'game_package_ids' ||
        key === 'game_ids' ||
        key === 'agent_tier_ids'
      ) {
        const ids = value
          .map((item: any) => {
            if (item === undefined || item === null || item === '') return null;
            if (typeof item === 'object' && item !== null && 'value' in item) {
              return item.value ? String(item.value).trim() : null;
            }
            return String(item).trim();
          })
          .filter(Boolean);
        if (ids.length > 0) {
          queryParams.append(key, ids.join(','));
        }
        return;
      }

      value.forEach((item) => {
        if (item === undefined || item === null || item === '') return;

        if (typeof item === 'object' && item !== null && 'value' in item) {
          // Handle { label, value } objects
          if (item.value) {
            queryParams.append(key, String(item.value).trim());
          }
        } else {
          // Handle primitives
          queryParams.append(key, String(item).trim());
        }
      });
      return;
    }

    if (value instanceof Date) {
      queryParams.append(key, formatDateToQueryString(value));
      return;
    }

    queryParams.append(key, String(value).trim());
  });

  return queryParams;
};

/**
 * Gets image dimensions from File (width and height).
 * @param file - Image file
 * @returns Promise with { width, height }
 */
export const getImageDimensions = (
  file: File
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
};
